import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Tooltip,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Info from 'components/utils/Alert/Info';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import {
  ROUTES,
  SUBSCRIPTION_TYPE,
  USER_PERMISSIONS,
  SelectFilterStyle,
  TRANSFER_STATUS,
  STATUS_COLOR_MAPPING,
} from 'const';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { AlertMessagesContext } from 'react-alert-messages';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import SyncIcon from '@mui/icons-material/Sync';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';
import StockTransferService from 'services/StockTransferService';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import StockTransferDetailsPopup from 'components/popups/StockTransferDetailsPopup';
import ShopsHelper from 'helpers/ShopsHelper';
import { selectedTabContext } from 'Context/SelectedTabContext';

const TRANSFER_TYPES = {
  incoming: 'incoming',
  outgoing: 'outgoing',
};

function StockTransferHistory() {
  const history = useHistory();
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { setSelectedTab, setSelectedSubTab, setSections } = useContext(selectedTabContext);

  const [filterStatus, setFilterStatus] = useState([]);
  const [filterTransferType, setFilterTransferType] = useState(TRANSFER_TYPES.incoming);
  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterStatus,
    filterTransferType,
  });
  const [transferDetails, setTransferDetails] = useState(null);

  const subscriptionType = shop?.subscription?.type;
  const userHasDamageWritePermission = user?.shop_permissions?.damage_permission === USER_PERMISSIONS.WRITE;

  const loadStockTransferHistory = useCallback(
    async (startDate = filterFromDate, endDate = filterToDate) => {
      setLoadingIndicator(true);
      try {
        const params = {
          start_time: DateTimeHelpers.getTimezoneOffsetDate(startDate),
          end_time: DateTimeHelpers.getTimezoneOffsetDate(endDate),
          status: filterStatus.join(','),
          type: filterTransferType,
        };
        const resp = await StockTransferService.getStockTransferHistory(params);
        setData(resp);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage, filterFromDate, filterToDate, filterStatus, filterTransferType]
  );

  const generateStockTransferHistory = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('STOCK_TRANSFER_HISTORY');

    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);

    loadStockTransferHistory(dynamicStartDate, dynamicEndDate);
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
      filterStatus,
      filterTransferType,
    });
  };

  const isAnyChangeOnStockDamageFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterStatus !== prevFilterData.filterStatus ||
      filterTransferType !== prevFilterData.filterTransferType
    );
  }, [
    filterFromDate,
    filterToDate,
    filterStatus,
    filterTransferType,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterStatus,
    prevFilterData.filterTransferType,
  ]);

  const _getFilteredTransfers = (datas, searchKey) => {
    if (!searchKey) {
      return datas;
    }

    return datas.filter(
      (dta) =>
        dta.to_shop.shop_name.toLowerCase().includes(searchKey.toLowerCase()) ||
        dta.created_at.includes(searchKey.toLowerCase())
    );
  };

  useEffect(() => {
    setSelectedTab('stock-transfer');
    setSelectedSubTab(ROUTES.STOCK_TRANSFER);
    setSections((prev) => ({ ...prev, operations: true, stockTransfer: true }));
    generateStockTransferHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnStockDamageFilters());
  }, [isAnyChangeOnStockDamageFilters, isShowGenerateButton]);

  const headerData = [
    {
      label: tCommon('Shop'),
      id: 'shop',
      type: 'callback',
      viewRender: (item) =>
        filterTransferType === TRANSFER_TYPES.incoming
          ? item.from_shop?.shop_name || ''
          : item.to_shop?.shop_name || '',
    },
    {
      label: `${tCommon('Date')} & ${tCommon('Time')}`,
      id: 'created_at',
      type: 'callback',
      viewRender: (item) =>
        `${DateTimeHelpers.convertDateToIsoDMY(item.created_at)} ${DateTimeHelpers.convertTo12HourTime(
          item.created_at
        )}`,
    },
    {
      label: tCommon('Items'),
      id: 'items',
      type: 'callback',
      viewRender: (item) => item?.data?.items?.map((i) => i.name).join(', ') || '-',
    },
    {
      label: tCommon('Amount'),
      id: 'items',
      type: 'callback',
      viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item?.data?.payable || 0),
    },
    {
      label: tCommon('Status'),
      id: 'status',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Chip
            className={styles.chip}
            label={TRANSFER_STATUS[item.status]}
            color={STATUS_COLOR_MAPPING[item.status]}
          />
        );
      },
    },
    {
      label: tCommon('Actions'),
      id: 'download',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (item) => (
        <Grid className={styles.btnGroup}>
          <Tooltip title={tCommon('View Details')}>
            <IconButton
              onClick={() => {
                if (item?.from_shop?.id !== shop.id || item.status === 'RECEIVED' || item.status === 'REJECTED') {
                  setTransferDetails(item);
                  return;
                } else {
                  history.push(`${ROUTES.EDIT_STOCK_TRANSFER.replace(':id', item.id)}`);
                }
              }}
            >
              <RemoveRedEyeIcon className={styles.iconBtn} />
            </IconButton>
          </Tooltip>
        </Grid>
      ),
    },
  ];

  const filteredTransfers = _getFilteredTransfers(data, searchText);

  return (
    <>
      <div className={styles.contentWrapper}>
        <div className={styles.titleSec}>
          <h2 className={styles.title}>
            {tCommon('Stock Transfer')}
            <span className={styles.menuTitle}>{tCommon('Management')}</span>
          </h2>
        </div>

        {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
          <RestrictionInfo
            title={tCommon('Feature not available')}
            content={tCommon('To get stock damage options upgrade subscription to premium')}
          />
        )}
        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <>
            <div className={styles.changeable}>
              <div className={styles.filterSec}>
                <div className={styles.headTitle}>
                  <h2 className={styles.subTitle}>
                    {tCommon('Stock transfer history from {{from}} to {{to}}', {
                      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                      to: DateTimeHelpers.convertDateToDMY(filterToDate),
                    })}
                  </h2>
                </div>
                <div className={styles.filerInputSec}>
                  {userHasDamageWritePermission && (
                    <Link to={ROUTES.NEW_STOCK_TRANSFER} style={{ textDecorationColor: 'transparent' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        className={styles.actionBtn}
                        style={{ backgroundColor: '#ff851b' }}
                      >
                        <NoteAddIcon className={styles.actionBtnIcon} />
                        {tCommon('New Transfer')}
                      </Button>
                    </Link>
                  )}
                  <div className={styles.searchSec}>
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                      }}
                      className={styles.searchInput}
                      placeholder={tCommon('Search items')}
                    />
                    <Tooltip title={tCommon('Search')} placement="bottom">
                      <IconButton>
                        <SearchIcon className={styles.searchIcon} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className={styles.actionButtons}>
                <div className={styles.filterDiv}>
                  <DateTimeRange
                    setFilterFromDate={setFilterFromDate}
                    setFilterToDate={setFilterToDate}
                    storageKey="STOCK_TRANSFER_HISTORY"
                  />
                </div>
                <div className={styles.filterDiv}>
                  <div style={{ paddingBottom: '4px' }}>
                    <label className={styles.label}>{tCommon('Status')}</label>
                  </div>
                  <div>
                    <FormControl>
                      <Select
                        labelId="demo-multiple-checkbox-label"
                        id="demo-multiple-checkbox"
                        multiple
                        displayEmpty
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        input={<OutlinedInput />}
                        style={{ width: 135, height: 40 }}
                        renderValue={(filterStatus) => {
                          if (filterStatus.length === 0) return <span>{tCommon('All')}</span>;
                          const selectedNames = filterStatus.map((st) => TRANSFER_STATUS[st]);
                          return selectedNames.join(', ');
                        }}
                        MenuProps={SelectFilterStyle}
                      >
                        {Object.entries(TRANSFER_STATUS).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            <Checkbox checked={filterStatus.includes(key)} color="primary" />
                            <ListItemText primary={value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
                <div className={styles.filterDiv}>
                  <div style={{ paddingBottom: '4px' }}>
                    <label className={styles.label}>{tCommon('Transfer Type')}</label>
                  </div>
                  <div>
                    <TextField
                      select
                      name="billNo"
                      size="small"
                      variant="outlined"
                      color="primary"
                      style={{ width: 135 }}
                      value={filterTransferType}
                      onChange={(event) => {
                        setFilterTransferType(event.target.value);
                      }}
                    >
                      <MenuItem value={TRANSFER_TYPES.outgoing}>{tCommon(TRANSFER_TYPES.outgoing)}</MenuItem>
                      <MenuItem value={TRANSFER_TYPES.incoming}>{tCommon(TRANSFER_TYPES.incoming)}</MenuItem>
                    </TextField>
                  </div>
                </div>

                <div className={styles.filterDiv}>
                  <div style={{ paddingBottom: '4px' }}>
                    <label className={styles.label}>{tCommon('Generate Report')}</label>
                  </div>
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      className={styles.actionBtn}
                      style={{ backgroundColor: '#00a65a' }}
                      onClick={generateStockTransferHistory}
                      disabled={isShowGenerateButton === false}
                    >
                      <SyncIcon className={styles.actionBtnIcon} />
                      {tCommon('Generate')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            {isShowGenerateButton === null ? (
              <Info
                title={tCommon('Set filters and click generate button')}
                content={tReport(
                  'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
                )}
              />
            ) : (
              (data || loadingIndicator) &&
              ((filteredTransfers && filteredTransfers.length) || loadingIndicator ? (
                <div className={styles.tableContainer}>
                  <Grayout open={isShowGenerateButton} />
                  <DataTable columns={headerData} rows={filteredTransfers || []} isLoading={loadingIndicator} />
                </div>
              ) : (
                !loadingIndicator && (
                  <Info
                    title={tCommon('No stock transfers found')}
                    content={tCommon("You don't have any stock transfers to list with the selected filters.")}
                  />
                )
              ))
            )}
          </>
        )}
      </div>
      {transferDetails && (
        <StockTransferDetailsPopup
          handleClose={() => setTransferDetails(null)}
          handleSuccess={() => {
            setTransferDetails(null);
            loadStockTransferHistory();
          }}
          transferData={transferDetails}
        />
      )}
    </>
  );
}

export default withConsoleBase(StockTransferHistory);
